if (!customElements.get('track-order')) {
  class TrackOrder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.alertEl = this.querySelector('.to-alert');
      this.alertTextEl = this.alertEl ? this.alertEl.querySelector('.to-alert__text') || this.alertEl : null;
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.result = this.querySelector('.to-result');
      this.form.addEventListener('submit', (evt) => this.submit(evt));
    }

    async submit(evt) {
      evt.preventDefault();
      this.hideAlert();
      if (!this.form.reportValidity()) return;

      const endpoint = this.dataset.endpoint;
      if (!endpoint) {
        this.showAlert('This page is not connected yet. Please reach out to us by email instead.');
        return;
      }

      this.submitBtn.classList.add('is-loading');
      this.submitBtn.disabled = true;
      try {
        const res = await fetch(endpoint, { method: 'POST', body: new FormData(this.form) });
        const data = await res.json().catch(() => null);
        if (data && data.ok && data.order) {
          this.renderResult(data.order);
        } else {
          this.result.hidden = true;
          this.showAlert((data && data.message) || 'Something went wrong. Please try again in a minute.');
        }
      } catch (err) {
        this.result.hidden = true;
        this.showAlert('We could not reach the order service. Please check your connection and try again.');
      } finally {
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.disabled = false;
      }
    }

    renderResult(order) {
      const r = this.result;
      r.innerHTML = '';

      const head = this.el('div', 'to-result__head');
      head.append(
        this.el('strong', 'to-result__name', `Order ${order.name}`),
        this.el('span', 'to-result__placed', `Placed ${this.fmtDate(order.placedAt)}`)
      );
      r.append(head);

      if (order.stage === 'canceled') {
        r.append(
          this.el(
            'p',
            'to-canceled',
            'This order has been canceled. If that seems wrong, please contact our support team.'
          )
        );
      } else {
        const steps = ['Order placed', 'Processing', 'Shipped', 'Delivered'];
        const reached = { processing: 1, shipped: 2, delivered: 3 }[order.stage] || 1;
        const list = this.el('ol', 'to-steps');
        steps.forEach((label, i) => {
          const li = this.el(
            'li',
            'to-step' + (i <= reached ? ' is-done' : '') + (i === reached ? ' is-current' : '')
          );
          li.append(this.el('span', 'to-step__dot'), this.el('span', 'to-step__label', label));
          list.append(li);
        });
        r.append(list);

        const delivered = order.stage === 'delivered' && order.fulfillments.map((f) => f.deliveredAt).find(Boolean);
        const estimate = order.fulfillments.map((f) => f.estimatedDeliveryAt).find(Boolean);
        if (delivered) r.append(this.el('p', 'to-eta', `Delivered ${this.fmtDate(delivered)}`));
        else if (estimate) r.append(this.el('p', 'to-eta', `Estimated delivery ${this.fmtDate(estimate)}`));

        const tracking = order.fulfillments.flatMap((f) => f.tracking);
        if (tracking.length) {
          const wrap = this.el('div', 'to-tracking');
          wrap.append(this.el('span', 'label', tracking.length > 1 ? 'Tracking numbers' : 'Tracking number'));
          tracking.forEach((t) => {
            const text = [t.company, t.number].filter(Boolean).join(' ') || 'Track shipment';
            if (t.url) {
              const a = this.el('a', 'to-tracking__link', text);
              a.href = t.url;
              a.target = '_blank';
              a.rel = 'noopener';
              wrap.append(a);
            } else {
              wrap.append(this.el('span', 'to-tracking__link', text));
            }
          });
          r.append(wrap);
        }
      }

      if (order.items.length) {
        const wrap = this.el('div', 'to-items');
        wrap.append(this.el('span', 'label', 'In this order'));
        const ul = this.el('ul', 'to-items__list');
        order.items.forEach((item) => ul.append(this.el('li', '', `${item.quantity} × ${item.title}`)));
        wrap.append(ul);
        r.append(wrap);
      }

      r.hidden = false;
      r.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text) node.textContent = text;
      return node;
    }

    fmtDate(iso) {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }

    showAlert(text) {
      if (!this.alertEl) return;
      this.alertTextEl.textContent = text;
      this.alertEl.classList.add('bg-error-bg', 'text-error-text');
      this.alertEl.hidden = false;
      this.alertEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.alertEl.focus({ preventScroll: true });
    }

    hideAlert() {
      if (this.alertEl) this.alertEl.hidden = true;
    }
  }

  customElements.define('track-order', TrackOrder);
}
